/**
 * useAttributedAction — wraps a creation mutation in the ADMIN ATTRIBUTION
 * rule. The server requires `attributeTo` (a member's uid) when the caller is
 * a superadmin who is NOT a member of the creation; members never need it.
 *
 * `runAttributed(creation, doAction)`:
 *  - superadmin non-member → opens {@link AttributionModal}; on confirm calls
 *    `doAction(pickedUid)`; on cancel, nothing runs.
 *  - everyone else (creator, editor, member superadmin) → `doAction(undefined)`
 *    immediately.
 *
 * The hook awaits attribution/cancel and action completion. Handle failures
 * inside `doAction`, or await `runAttributed` in a try/catch. Modal-confirmed
 * actions propagate errors exactly like actions that need no attribution.
 * Render `attributionModal` somewhere in the page so the modal can appear.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import type { Creation } from '@djgg/creations';
import { isMemberOrAdmin } from '@djgg/creations';
import { useAuth } from '../services/context.js';
import { AttributionModal } from '../components/AttributionModal.js';

type AttributedCreation = Creation & { id: string };
type AttributedAction = (attributeTo?: string) => Promise<void>;

export interface UseAttributedAction {
  /** Run `doAction`, first collecting `attributeTo` when the caller needs it. */
  runAttributed: (creation: AttributedCreation, doAction: AttributedAction) => Promise<void>;
  /** Mount this in the page's JSX — null unless a pick is pending. */
  attributionModal: ReactNode;
}

export function useAttributedAction(): UseAttributedAction {
  const { user } = useAuth();
  const [pending, setPending] = useState<{
    creation: AttributedCreation;
    doAction: AttributedAction;
    finish: () => void;
    fail: (error: unknown) => void;
  } | null>(null);
  const pendingRef = useRef<typeof pending>(null);
  useEffect(() => {
    setPending(null);
    return () => { pendingRef.current?.finish(); pendingRef.current = null; };
  }, [user?.uid]);

  const runAttributed = useCallback(
    async (creation: AttributedCreation, doAction: AttributedAction): Promise<void> => {
      // Membership WITHOUT the admin shortcut: pass claims with no role, so
      // isMemberOrAdmin reduces to "is creator or in memberUids".
      const isMember = !!user && isMemberOrAdmin({ uid: user.uid }, creation);
      const needsAttribution = user?.role === 'superadmin' && !isMember;
      if (needsAttribution) {
        // Await the actual decision AND request, not merely opening a modal.
        // Callers can safely hold their submission lock until this settles.
        await new Promise<void>((finish, fail) => {
          pendingRef.current?.finish();
          const request = { creation, doAction, finish, fail };
          pendingRef.current = request;
          setPending(request);
        });
        return;
      }
      await doAction(undefined);
    },
    [user],
  );

  const attributionModal = pending ? (
    <AttributionModal
      creation={pending.creation}
      onCancel={() => {
        if (pendingRef.current !== pending) return;
        pendingRef.current = null; setPending(null); pending.finish();
      }}
      onConfirm={(uid) => {
        if (pendingRef.current !== pending) return;
        pendingRef.current = null;
        const { doAction, finish, fail } = pending;
        setPending(null);
        void doAction(uid).then(finish, fail);
      }}
    />
  ) : null;

  return { runAttributed, attributionModal };
}
